import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { ToastRegionProps, ToastRecord } from '../../../types/DesignSystem/Toast';
import Toast from './Toast';
import { getToasts, subscribe } from './toastStore';
import { withClassName } from '../cn';

/** Which announcer a tone belongs in. */
const isUrgent = (t: ToastRecord) => t.tone === 'danger' || t.tone === 'warning';

/**
 * Identity of "this message, this time".
 *
 * **One function because it was two expressions, and that was a latent bug.** The
 * key was inlined at both the record-it and the forget-it site. Change one and not
 * the other and the two sets stop agreeing: every toast then looks unannounced on
 * every store change, so the announcers re-fire and a screen reader repeats itself.
 * Found by a red-green probe that edited one site and watched the suite stay green
 * for the wrong reason.
 *
 * `seq` rather than `id` alone is what makes a REPLACE audible — see `ToastRecord`.
 */
const announceKey = (t: ToastRecord) => `${t.id}:${t.seq}`;

/**
 * § 342 — what an announcer holds: the text AND the announcement it belongs to.
 *
 * The announcers used to hold a bare string. A second identical message ("Saved",
 * then "Saved" again from the next save) set the same string, React skipped the
 * update, the live region's DOM never changed, and a screen reader heard nothing.
 * Rendering the text in a node keyed by `announceKey` makes every announcement its
 * own insertion, which is what a live region reacts to.
 */
interface Announcement {
  text: string;
  key: string;
}

const SILENT: Announcement = { text: '', key: '' };

/**
 * The toast viewport. Mount **once**, near the app root.
 *
 * ```tsx
 * <ToastRegion />          // then anywhere: toast.success('Saved')
 * ```
 *
 * **Stacking context.** `tw-z-toast` compiles to 6000. § 204 picked 2040 by grepping
 * what was actually on the page; § 283 (2026-09-13) raised the whole ladder, because
 * that grep had a hole in it — the LEGACY `#modal-root` was never measured, and at
 * z-5000 it swallowed both toasts and DS dialogs raised from inside a legacy modal.
 * The neighbours, re-measured:
 *
 * | layer | z |
 * |---|---|
 * | DeltaTable sticky `thead` | 1001 |
 * | `#sidepanel-portal` in `Layout.tsx` | 2030 |
 * | **legacy `#modal-root`** in `App.tsx` | **5000** |
 * | `Dialog`, `SidePanel` panel (`tw-z-overlay`) | 5050 |
 * | **toasts** (`tw-z-toast`) | **6000** |
 * | `Tooltip`, `HoverTooltip` (`tw-z-tooltip`) | 9999 |
 *
 * A toast must clear the dialog it was raised from — "Saved" behind the modal that
 * saved is the same as no toast, and for every `ModalContainer` in the app that is
 * exactly what happened until § 283. It stays under tooltips, which are
 * pointer-attached and belong on top of everything.
 *
 * **The announcers are always mounted, even with nothing on screen.** That is the
 * whole reason this component renders a portal unconditionally rather than returning
 * `null` when the list is empty: a live region has to exist *before* text arrives in
 * it, or the screen reader has nothing subscribed and says nothing. An empty
 * `<div aria-live>` costs nothing; getting this wrong costs the entire feature for
 * the users it was built for.
 *
 * Two regions rather than one with a switched `aria-live`: changing that attribute on
 * a live region is unreliable, so `polite` and `assertive` are separate elements and
 * each toast is routed to the right one.
 */
const ToastRegion = React.forwardRef<HTMLDivElement, ToastRegionProps>(function ToastRegion({
  className = '',
  'data-testid': dataTestId = 'toast-region',
  ...rest
}, ref) {
  const toasts = useSyncExternalStore(subscribe, getToasts, getToasts);
  const [polite, setPolite] = useState<Announcement>(SILENT);
  const [assertive, setAssertive] = useState<Announcement>(SILENT);
  /* Keyed `id:seq`, not `id`. A replace reuses the id, so keying on id alone made
     "Saving…" → "Saved" silent to a screen reader. */
  const announced = useRef<Set<string>>(new Set());

  useEffect(() => {
    for (const t of toasts) {
      const key = announceKey(t);
      if (announced.current.has(key) || !t.open) continue;
      announced.current.add(key);
      const text = t.description ? `${t.title}. ${t.description}` : t.title;
      if (isUrgent(t)) setAssertive({ text, key });
      else setPolite({ text, key });
    }
    /* Forget keys whose toast is gone, so the same message can be raised again later
       and still announce. Without this the set grows for the life of the page and a
       repeated "Saved" goes unheard. */
    const live = new Set(toasts.map(announceKey));
    for (const key of announced.current) {
      if (!live.has(key)) announced.current.delete(key);
    }
  }, [toasts]);

  if (typeof document === 'undefined') return null;

  // `font-noto-sans`: portalled into document.body — same reasoning as Dialog.
  /* § 410 — `data-ds-top-layer` (`TOP_LAYER_ATTR`): the region sits above every modal, so it
     stays live while one makes the page inert. "Saved" from a dialog's save is read, and its
     Undo can be pressed. */
  return createPortal(
    <div {...rest} ref={ref} className={withClassName(`tw-font-noto-sans`, className)} data-testid={dataTestId} data-ds-top-layer="">
      {/* Always present. See the note above — this is the load-bearing part. */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="tw-sr-only"
        data-testid="toast-announcer-polite"
      >
        {polite.text && <span key={polite.key}>{polite.text}</span>}
      </div>
      <div
        aria-live="assertive"
        aria-atomic="true"
        className="tw-sr-only"
        data-testid="toast-announcer-assertive"
      >
        {assertive.text && <span key={assertive.key}>{assertive.text}</span>}
      </div>

      <ol
        /* `region` needs an accessible name, which `aria-label` gives it, so a screen
           reader user can jump to notifications by landmark.
           `pointer-events-none` on the column, re-enabled per toast: this is a fixed
           element and without it the empty space above the stack eats every click
           down the right side of the page. */
        role="region"
        aria-label="Notifications"
        className="tw-fixed tw-bottom-4 tw-right-4 tw-z-toast tw-flex tw-flex-col tw-gap-2 tw-pointer-events-none tw-m-0 tw-p-0 tw-list-none"
        data-testid="toast-region-list"
      >
        {/* Newest LAST, so it appears nearest the corner the eye is already on and
            older ones ride up. Appending also means an existing toast never moves,
            which matters because the dismiss buttons must not shuffle under the
            pointer. */}
        {toasts.map((t) => <Toast key={t.id} toast={t} />)}
      </ol>
    </div>,
    document.body,
  );
});

export default ToastRegion;
